import { useMutation, useQuery, useQueryClient } from 'react-query';
import feathersClient from '../../../apiClient';
import { getPaginatedResponse } from '../../../apiClient/utils';
import { TyTable, TyTableCell, TyTableHead, TyTableRow } from '@tymbe/ty-components/table';
import { TableHeadCellSortable } from '../../../components/Table/TableHeadCellSortable';
import { TableHead, TablePagination, useTablePagination } from '../../../components/Table';
import useTableSort from '../../../components/Table/useTableSort';
import Spinner from '../../../components/Spinner';
import { TyTableRowExpandable } from './components/TyTableRowExpandable';
import PersonName from '../../../components/dataEntity/PersonName';
import CompanyName from '../../../components/dataEntity/CompanyName';
import moment from 'moment';
import ResendMpsvRequestButton from '../mpsv/components/ResendMpsvRequestButton';
import ShowMpsvRequestButton from '../mpsv/components/ShowMpsvRequestButton';
import ChangeMpsvStatusButton from '../mpsv/components/ChangeMpsvStatusButton';
import StornoMpsvButton from '../mpsv/components/StornoMpsvButton';
import Button from '../../../components/buttons/Button';
import ChangeIdPpv from './components/ChangeIdPpv';
import { useState } from 'react';

interface JmhzPpvPageProps {
  query?: object;
}

const JmhzPpvPage = ({ query }: JmhzPpvPageProps) => {
  const queryClient = useQueryClient();
  const { addSort, sortString: sort } = useTableSort({
    initialSortField: 'jmhz_ppv.id',
    initialSort: -1,
  });
  const { setPageParams, pageSize, pageStart } = useTablePagination();
  const [field, sortOrder] = sort?.split('=') ?? ['jmhz_ppv.id', 1];

  const { data, isLoading } = useQuery({
    queryKey: [
      'jmhz',
      'ppv',
      {
        ...query,
        $sort: Number(sortOrder) ? { [field]: Number(sortOrder) } : undefined,
        $limit: pageSize,
        $skip: pageStart,
      },
    ] as const,
    queryFn: async ({ queryKey: [, , q] }) => {
      const result = await feathersClient.service('jmhz-ppv').find({
        query: {
          $leftJoinRelation: '[employee,contracts]',
          $eager: '[contracts.documentType]',
          // 'documentType.type': { $in: Object.values(ContractDocumentTypes) },
          ...q,
          $sort: {
            ...q.$sort,
            'contracts.valid_from': -1,
          },
        },
      });
      return getPaginatedResponse(result);
    },
  });
  const changeIdPpv = useMutation({
    mutationKey: 'changeIdPpv',
    mutationFn: async ({ id, newId }: { id: string, newId: string }) => {
      await feathersClient.service('jmhz-ppv').patch(id, { '_id': newId }, { query: { $noSelect: true }});
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['jmhz'] });
    },
  });

  return (
    <>
      <TyTable className="w-full">
        <TableHead>
          <TyTableRow>
            <TyTableCell className="w-7 border-r-transparent" />
            <TableHeadCellSortable {...addSort('jmhz_ppv.id')}>ID PPV</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('employer_id')}>Firma</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('employee_id')}>Tymber ID</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('employee.last_name')}>Tymber</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('contract_type')}>Typ smlouvy</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('group')}>Skupina</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('is_small')}>Malá</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('state')}>Stav</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('latest_data')}>Poslední data</TableHeadCellSortable>
            <TyTableCell className="w-10 border-r-transparent" />
          </TyTableRow>
        </TableHead>
        <tbody>
          {isLoading && (
            <TyTableRow>
              <TyTableCell colSpan={11} align="center">
                <Spinner show={isLoading} className="w-6" />
              </TyTableCell>
            </TyTableRow>
          )}
          {data?.data.length === 0 ? (
            <TyTableRow>
              <TyTableCell colSpan={11} align="center">
                Žádná data
              </TyTableCell>
            </TyTableRow>
          ) : null}

          {data?.data.map((item) => (
            <TyTableRowExpandable
              className="bg-bg h-10 [&>td:first-child]:border-r-transparent"
              key={item.id}
              cols={[
                (expanded, cantExpand) => (cantExpand ? '!' : expanded ? '˅' : '˃'),
                item.id,
                <CompanyName company_id={item.employer_id} link />,
                item.employee_id,
                <PersonName person_id={item.employee_id} link />,
                item.contract_type,
                item.group,
                item.is_small ? 'Ano' : 'Ne',
                item.state,
                JSON.stringify(item.latest_data),
                () => {
                  return (
                    <div className="flex flex-row gap-1 justify-end">
                      <ChangeIdPpv onSubmit={(data) => changeIdPpv.mutateAsync({id: item.id, newId: data.idPpv})}>
                        Změnit ID PPV
                      </ChangeIdPpv>
                    </div>
                  );
                },
              ]}
              cantExpand={!item.contracts?.length}
            >
              {item.contracts?.map((contract) => (
                <TyTableRow key={contract.id}>
                  <TyTableCell colSpan={11} className="!p-0 !border-w-0">
                    <TyTable className="[&_td]:p-1 [&_th]:p-1">
                      <TableHead>
                        <TyTableRow>
                          <TyTableHead>ID</TyTableHead>
                          <TyTableHead>Typ smlouvy</TyTableHead>
                          <TyTableHead>Platnost od</TyTableHead>
                          <TyTableHead>Platnost do</TyTableHead>
                        </TyTableRow>
                      </TableHead>
                      <tbody>
                        <TyTableRow>
                          <TyTableCell>{contract.id}</TyTableCell>
                          <TyTableCell>{contract.documentType?.type}</TyTableCell>
                          <TyTableCell>{moment(contract.valid_from).format('DD.MM.YYYY')}</TyTableCell>
                          <TyTableCell>{moment(contract.valid_to).format('DD.MM.YYYY')}</TyTableCell>
                        </TyTableRow>
                      </tbody>
                    </TyTable>
                  </TyTableCell>
                </TyTableRow>
              ))}
            </TyTableRowExpandable>
          ))}
        </tbody>
      </TyTable>
      <TablePagination {...setPageParams()} rowsCount={data?.total} />
    </>
  );
};

export default JmhzPpvPage;
